import { formatFullName } from "utils/formatters/NameFormatters";
import type { UserDto } from "client/api/UsersApi";
import axiosClient from "../axios-client";

export interface ListUserGrantsDto<T extends UserGrantDto> {
  directUserGrants: Array<T>;
}

export interface UserGrantDto {
  id: string;
  role: RoleDto;
  user: UserDto;
}

export interface ServerLicenseUserGrantDto extends UserGrantDto {
  serverLicenseId: string;
}

export interface CloudSubscriptionUserGrantDto extends UserGrantDto {
  cloudSubscriptionId: string;
}

export interface SubscriptionGroupUserGrantDto extends UserGrantDto {
  subscriptionGroupId: string;
  subscriptionGroupName: string;
}

export interface RoleDto {
  id: string;
  name: string;
}

export const formatUserName = (user: UserDto): string => {
  if (!user.firstName) {
    return user.email ?? "(Unknown)";
  }

  return `${formatFullName(user)} (${user.email})`;
};
export class BaseUserGrantsApi<TUserGrant extends UserGrantDto, TCreateRequest extends CreateUserGrantRequest> {
  constructor(
    private assetUrl: string,
    private createRequestFactory: (assetId: string, userId: string, roleId: string) => TCreateRequest
  ) {}

  public async listUserGrants(assetId: string): Promise<ListUserGrantsDto<TUserGrant>> {
    const response = await axiosClient.get<ListUserGrantsDto<TUserGrant>>(this.buildUrl(assetId));
    return response.data;
  }

  public async createUserGrant(assetId: string, userId: string, roleId: string): Promise<TUserGrant> {
    const request = this.createRequestFactory(assetId, userId, roleId);
    const response = await axiosClient.post<TUserGrant>(this.buildUrl(assetId), request);
    return response.data;
  }

  public async deleteUserGrant(assetId: string, userGrantId: string): Promise<void> {
    await axiosClient.delete(`${this.buildUrl(assetId)}/${userGrantId}`);
  }

  private buildUrl(assetId: string): string {
    return `${this.assetUrl}/${assetId}/user-grants`;
  }
}

export const serverLicenseUserGrantsApi = new BaseUserGrantsApi<
  ServerLicenseUserGrantDto,
  CreateUserGrantRequest & { serverLicenseId: string }
>("server-licenses", (id, userId, roleId) => {
  return {
    userId,
    roleId,
    serverLicenseId: id,
  };
});

export const cloudSubscriptionUserGrantsApi = new BaseUserGrantsApi<
  CloudSubscriptionUserGrantDto,
  CreateUserGrantRequest & { cloudSubscriptionId: string }
>("cloud-subscriptions", (id, userId, roleId) => {
  return {
    userId,
    roleId,
    cloudSubscriptionId: id,
  };
});

export const subscriptionGroupUserGrantsApi = new BaseUserGrantsApi<
  UserGrantDto,
  CreateUserGrantRequest & { groupId: string }
>("subscription-groups", (id, userId, roleId) => {
  return {
    userId,
    roleId,
    groupId: id,
  };
});

export type CreateUserGrantRequest = { userId: string; roleId: string };
